import { call, put, take, takeEvery, takeLatest, select } from 'redux-saga/effects';
import AxiosFactory from 'utils/axiosFactory';
import { Config, ENVIRONMENT } from 'utils/webappConfig';

import {
  getQcsSyncResources,
  qcsSyncCreateResource,
  qcsSyncUpdateResource,
  qcsSyncDeleteResource,
} from 'data/qcsSyncResourceSagaHelpers';

import { mkQcsSyncResourceConfig } from 'data/qcsSyncResourceTypes';
import { getLogger } from 'utils/logger';
import { getFeatureFlags, getFeatureFlag } from 'utils/featureFlags/selectors';

import * as transactionsActions from 'data/transactions/actions';
import { v4 as uuidv4 } from 'uuid';
import { mkFilter } from 'data/filters/types';
import * as filterAction from 'data/filters/actions';
import * as actions from './actions';
import * as filterSaga from '../filters/sagas';
import { transformResponseToSpendingWatchLists, transformSpendingWatchListToRequestData } from './transformers';
import { getLastSyncDate } from './selectors';
import { mkSpendingWatchList } from './types';

const log = getLogger('data/spendingWatchList/sagas');

// ===============================================================================================
// SAGAS functions for watch lists.  These are the functions responsible for communicating directly
// with external endpoints asynchronously.

// ====================================================
// WORKER SAGA

function* createWatchListWorker(action: any) {
  const title = action.payload.get('title');
  const targetAmt = action.payload.get('targetAmount');
  const emoji = action.payload.get('emoji');

  // Get filterItems from the payload and transform it by sending it through mkFilter
  const filterItems = mkFilter({
    id: null,
    clientId: uuidv4().toUpperCase(),
    filterItems: action.payload.get('filterItems'),
  });

  // Send the filterItems to the createFilter Saga and capture the returned filterID
  const filterID = yield* filterSaga.createSpendingWatchListFilterAction(filterItems);

  // Attached returned filter Id to spending watch list
  const newWatchList = mkSpendingWatchList({
    id: null,
    clientId: uuidv4().toUpperCase(),
    name: title,
    isDeleted: null,
    filterId: filterID.get('id'),
    targetAmount: targetAmt,
    emoji,
  });

  yield put(actions.createSpendingWatchList(newWatchList));
}

function* deleteWatchListWorker(action: any) {
  const { payload: resource } = action;

  if (resource.filterToDelete) {
    yield put(filterAction.deleteFilter(resource.filterToDelete));
  }
  yield put(actions.deleteSpendingWatchList(resource.spendingWatchListToDelete));
}
// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* createWatchListActionWatcher() {
  yield takeLatest(actions.createWatchList, createWatchListWorker);
}

export function* deleteWatchListActionWatcher() {
  yield takeEvery(actions.deleteWatchList, deleteWatchListWorker);
}

export function* createWatchListSuccessActionWatcher() {
  while (true) {
    const action = yield take(actions.createSpendingWatchListSuccess);
    if (action) {
      yield put(actions.getSpendingWatchLists());
    }
  }
}

// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'spendingWatchList',
  resourceBaseUrl: '/spending-watchlist',
  getLastSyncDate,

  transformResponseToResources: transformResponseToSpendingWatchLists,
  transformResourceToRequestData: transformSpendingWatchListToRequestData,

  successAction,
  failureAction,
});

function * getSpendingWatchlistNoCalc(action) {
  yield call(
    getQcsSyncResources,
    mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
    action
  );
}

function* getSpendingWatchListCalc(action) {
  const webSocketEnabled = yield select(getFeatureFlag, 'webSocketQcs');

  try {
    log.log('getSpendingWatchListAction: server calculation start...');
    const qcsAxios = AxiosFactory.get('qcs');
    let requestUrl;
    if (webSocketEnabled) {
      requestUrl = `${Config.environments[ENVIRONMENT].services_url}/spending-watchlist/calc/async`;
    } else {
      requestUrl = `${Config.environments[ENVIRONMENT].services_url}/spending-watchlist/calc`;
    }
    const response = yield call(qcsAxios.get, requestUrl);
    log.log('getSpendingWatchListAction: server calculation complete...\nresponse = ', response);
  } catch (error) {
    log.log('getSpendingWatchListAction: server calculation failed...\nerror = ', error);
  }

  if (!webSocketEnabled) {
    yield call(
      getQcsSyncResources,
      mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
      action
    );
  }
}

export function* getSpendingWatchListInitialActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getSpendingWatchListsInitial);
    yield call(getSpendingWatchlistNoCalc, action);
  }
}

export function* getSpendingWatchListActionWatcher() {
  // using a vanilla saga pattern so we only process a single get request
  //   - takeEvery would process every request
  //   - takeLatest would cancel any current request and start request again
  while (true) {
    const action = yield take(actions.getSpendingWatchLists);
    yield call(getSpendingWatchListCalc, action);
  }
}

export function* createSpendingWatchListActionWatcher() {
  const config = mkResourceConfig(actions.createSpendingWatchListSuccess, actions.createSpendingWatchListFailure);
  yield takeEvery(actions.createSpendingWatchList, qcsSyncCreateResource, config);
}

export function* updateSpendingWatchListActionWatcher() {
  const config = mkResourceConfig(actions.updateSpendingWatchListSuccess, actions.updateSpendingWatchListFailure);
  yield takeEvery(actions.updateSpendingWatchList, qcsSyncUpdateResource, config);
}

export function* deleteSpendingWatchListActionWatcher() {
  const config = mkResourceConfig(actions.deleteSpendingWatchListSuccess, actions.deleteSpendingWatchListFailure);
  yield takeEvery(actions.deleteSpendingWatchList, qcsSyncDeleteResource, config);
}

function* transactionModified(action) {
  const featureFlags = yield select(getFeatureFlags);
  if (featureFlags && featureFlags.get('spendingWatchList')
    && action && action.meta && action.meta.context
    && action.meta.context === 'Reg_SpendingWatchListGraph'
  ) {
    yield put(actions.getSpendingWatchLists());
  }
}

function* transactionModifiedActionWatcher() {
  yield takeEvery(transactionsActions.createTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.updateTransactionsSuccess, transactionModified);
  yield takeEvery(transactionsActions.deleteTransactionsSuccess, transactionModified);
}

// ====================================================
// EXPORTS

export default [
  createWatchListActionWatcher,
  deleteWatchListActionWatcher,
  createWatchListSuccessActionWatcher,
  getSpendingWatchListInitialActionWatcher,
  getSpendingWatchListActionWatcher,
  createSpendingWatchListActionWatcher,
  updateSpendingWatchListActionWatcher,
  deleteSpendingWatchListActionWatcher,
  transactionModifiedActionWatcher,
];
